import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react';

import {
  InputBox,
  LbDatePicker,
  LbInput,
  LbSelect,
  HeightInput,
  ReadOnlyInput,
} from '@infoods/lifebase-ui';

import classes from './PersonalInfoStep.module.scss';
import {
  HiddenInput,
  PhoneNumberInput,
} from '../../../../../../shared/components/form-components';
import {
  validateAge,
  validateEmail,
  validateOnlyLettersAndSpaces,
} from '../../../../../../shared/utils/reactHookFormUtils';
import { genderOptions } from '../../../../../../shared/constants/user-info/gender';
import { SubmitButton } from '../../../components/SubmitButton/SubmitButton';
import { TermsAcceptCheckbox } from '../../../../../../shared/components/TermsAcceptCheckbox/TermsAcceptCheckbox';
import { scanIqStore } from '../../../../../stores/scanIqStore';
import { entitySignUpStore } from '../../../../../../shared/stores/entity/entitySignUpStore';
import { ScanIqCustomerSetupPersonalData } from '../../../../../interfaces/ScanIqCustomerSetupInfo';
import { scanIqCustomerService } from '../../../../../services/scanIqCustomerService';
import { getFilteredTruthyFieldsObj } from '../../../../../../shared/utils/sortingAndGroupingUtil';

type PersonalInfoFormProps = {
  onCustomerCreated: () => void;
};
/*
  after 2fa step backend create customer with phone or email (based on verification channel)
  we should omit change phone or email if it is already set
*/
const PersonalInfoStep = observer(({ onCustomerCreated }: PersonalInfoFormProps) => {
  const { initialCustomer, setCustomer } = scanIqStore;
  const { entityName } = entitySignUpStore;

  const { control, register, handleSubmit } = useForm<ScanIqCustomerSetupPersonalData>({
    shouldUnregister: true, // remove fields that are not in the form (activeSubscription, setupStatus, etc.)
    defaultValues: getFilteredTruthyFieldsObj(initialCustomer || {}),
  });

  const [isCommunicationsAccepted, setIsCommunicationsAccepted] = useState(false);
  const [isMarketingAccepted, setIsMarketingAccepted] = useState(false);

  const generalInputProps = {
    control,
    size: 'small',
    variant: 'standard',
  } as const;

  const onSubmit = handleSubmit(async (data) => {
    // Note: We should always update the customer on this step because 2FA step always creates customer record on backend when customer does not exist
    const updatedCustomer = await scanIqCustomerService.updateCustomer(data);

    setCustomer(updatedCustomer);
    onCustomerCreated();
  });

  return (
    <form onSubmit={onSubmit} className={classes.container} noValidate>
      <InputBox title="Personal Data">
        <HiddenInput register={register} name="entityId" />
        <LbInput
          {...generalInputProps}
          name="firstName"
          label="First Name"
          rules={{
            required: 'First Name is required',
            validate: { validateOnlyLettersAndSpaces },
          }}
        />
        <LbInput
          {...generalInputProps}
          name="lastName"
          label="Last Name"
          rules={{
            required: 'Last Name is required',
            validate: { validateOnlyLettersAndSpaces },
          }}
        />
        {initialCustomer?.email ? (
          <ReadOnlyInput {...generalInputProps} name="email" label="Email address" />
        ) : (
          <LbInput
            {...generalInputProps}
            name="email"
            label="Email address"
            type="email"
            rules={{ required: 'Email is required', validate: { validateEmail } }}
          />
        )}
        {initialCustomer?.phone ? (
          <ReadOnlyInput {...generalInputProps} name="phone" label="Cell Phone" />
        ) : (
          <PhoneNumberInput
            {...generalInputProps}
            name="phone"
            label="Cell Phone"
            rules={{ required: 'Cell Phone is required' }}
          />
        )}
        <LbDatePicker
          {...generalInputProps}
          name="dateOfBirth"
          label="Date of Birth"
          helperText="You need to be at least 14 years to join the SWYMO"
          openTo="year"
          rules={{
            required: 'Date of Birth is required',
            validate: { validateMinAge: (value) => validateAge(value, 14) },
          }}
        />
        <LbSelect
          {...generalInputProps}
          name="gender"
          label="Gender"
          options={genderOptions}
          rules={{ required: 'Gender is required' }}
        />
        <HeightInput
          {...generalInputProps}
          name="height"
          label="Height"
          rules={{ required: 'Height is required' }}
        />
      </InputBox>
      <div>
        <TermsAcceptCheckbox
          isCommunicationsAccepted={isCommunicationsAccepted}
          isMarketingAccepted={isMarketingAccepted}
          setIsCommunicationsAccepted={setIsCommunicationsAccepted}
          setIsMarketingAccepted={setIsMarketingAccepted}
          entityName={entityName}
        />
        <SubmitButton />
      </div>
    </form>
  );
});

export { PersonalInfoStep };
